// PLE-478 / PLE-392: first-touch attribution columns for public.profiles.
// Values come from the first-touch `marketing_attribution` cookie (written once
// by instrumentation-client) and, for email signup, from the snapshot the form
// sends in the action body when that cookie is missing from the request header.
// The Trackdesk `trakdesk_cid` cookie supplies affiliate id. Written once per
// profile at signup, never overwritten.

import { z } from "zod"

export const FIRST_TOUCH_MAX_ACCOUNT_AGE_MS = 7 * 24 * 60 * 60 * 1000

export type ProfileFirstTouchColumns = {
  first_touch_utm_source?: string
  first_touch_utm_medium?: string
  first_touch_utm_campaign?: string
  first_touch_utm_content?: string
  first_touch_utm_term?: string
  first_touch_referrer?: string
  first_touch_landing_path?: string
  first_touch_affiliate_id?: string
}

const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const

function clean(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim().slice(0, 256)
  return trimmed || undefined
}

export function parseTrackdeskCid(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(decodeURIComponent(raw))
    return clean(parsed?.cid)
  } catch {
    return undefined
  }
}

export function buildProfileFirstTouch(
  attribution: Record<string, string>,
  trackdeskCookie?: string | null
): ProfileFirstTouchColumns {
  const out: ProfileFirstTouchColumns = {}
  for (const k of UTM_KEYS) {
    const v = clean(attribution[`utm_${k}`] ?? attribution[`initial_utm_${k}`])
    if (v) out[`first_touch_utm_${k}`] = v
  }
  const referrer = clean(attribution.initial_referrer)
  if (referrer) out.first_touch_referrer = referrer
  const landing = clean(attribution.initial_landing_path)
  if (landing) out.first_touch_landing_path = landing
  const affiliate = parseTrackdeskCid(trackdeskCookie)
  if (affiliate) out.first_touch_affiliate_id = affiliate
  return out
}

// Raw cookie values (marketing_attribution / legacy trafficstars_attribution,
// trakdesk_cid) -> columns. Never throws; unparseable cookies yield {}.
export function buildProfileFirstTouchFromCookies(
  attributionRaw: string | undefined | null,
  trackdeskRaw?: string | null
): ProfileFirstTouchColumns {
  const attribution: Record<string, string> = {}
  if (attributionRaw) {
    for (const raw of [attributionRaw, safeDecode(attributionRaw)]) {
      try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          for (const [k, v] of Object.entries(parsed)) {
            if (typeof v === "string") attribution[k] = v
          }
          break
        }
      } catch {
        // not JSON; try the decoded form
      }
    }
  }
  return buildProfileFirstTouch(attribution, trackdeskRaw)
}

function safeDecode(v: string): string {
  try {
    return decodeURIComponent(v)
  } catch {
    return v
  }
}

// A non-string field is dropped on its own; it does not void the snapshot.
const snapshotField = z.string().optional().catch(undefined)

// Unknown keys are stripped by z.object.
const clientFirstTouchSchema = z.object({
  utm_source: snapshotField,
  utm_medium: snapshotField,
  utm_campaign: snapshotField,
  utm_content: snapshotField,
  utm_term: snapshotField,
  initial_utm_source: snapshotField,
  initial_utm_medium: snapshotField,
  initial_utm_campaign: snapshotField,
  initial_utm_content: snapshotField,
  initial_utm_term: snapshotField,
  initial_referrer: snapshotField,
  initial_landing_path: snapshotField,
})

function cleanReferrer(value: string): string | undefined {
  try {
    return clean(new URL(value).origin)
  } catch {
    return undefined
  }
}

function cleanLandingPath(value: string): string | undefined {
  const path = value.split("?")[0]?.split("#")[0] ?? ""
  if (!path.startsWith("/") || path.startsWith("//")) return undefined
  return clean(path)
}

// Trust boundary for the signup action body. Unknown keys are dropped.
// Referrer is stored as an origin only, so a query string never lands on the profile.
export function sanitizeClientFirstTouch(input: unknown): Record<string, string> {
  const parsed = clientFirstTouchSchema.safeParse(input)
  if (!parsed.success) return {}
  const out: Record<string, string> = {}
  for (const [key, raw] of Object.entries(parsed.data)) {
    if (raw === undefined) continue
    const value = key === "initial_referrer"
      ? cleanReferrer(raw)
      : key === "initial_landing_path"
        ? cleanLandingPath(raw)
        : clean(raw)
    if (value) out[key] = value
  }
  return out
}

// Cookie header wins as a whole snapshot. The client body is used only when
// that snapshot is missing, so a later signup-page campaign cannot fill an
// earlier untagged visit. Affiliate id still comes from the Trackdesk cookie.
export function resolveSignupFirstTouch(
  cookieRaw: string | undefined | null,
  trackdeskRaw: string | undefined | null,
  clientSnapshot: unknown
): ProfileFirstTouchColumns {
  const fromCookie = buildProfileFirstTouchFromCookies(cookieRaw, trackdeskRaw)
  const cookieHasVisit = Object.keys(fromCookie).some(
    (key) => key !== "first_touch_affiliate_id"
  )
  if (cookieHasVisit) return fromCookie
  return buildProfileFirstTouch(sanitizeClientFirstTouch(clientSnapshot), trackdeskRaw)
}

function readCookieValue(name: string): string | null {
  try {
    const prefix = `${name}=`
    const cookie = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(prefix))
    return cookie ? cookie.slice(prefix.length) : null
  } catch {
    return null
  }
}

function attributionRecordFromRaw(raw: string | null): Record<string, string> {
  if (!raw) return {}
  for (const candidate of [raw, safeDecode(raw)]) {
    try {
      const parsed = JSON.parse(candidate)
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) continue
      const out: Record<string, string> = {}
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === "string") out[key] = value
      }
      return out
    } catch {
      // try the decoded form
    }
  }
  return {}
}

// Browser snapshot for the email signup action. An existing first-touch cookie
// wins whole; otherwise the live URL and referrer are the snapshot.
export function collectBrowserFirstTouch(): Record<string, string> {
  if (typeof document === "undefined") return {}
  const persisted = sanitizeClientFirstTouch(
    attributionRecordFromRaw(readCookieValue("marketing_attribution"))
  )
  if (Object.keys(persisted).length > 0) return persisted
  const live: Record<string, string> = {}
  try {
    const params = new URLSearchParams(window.location.search)
    for (const key of UTM_KEYS) {
      const value = clean(params.get(`utm_${key}`))
      if (value) live[`utm_${key}`] = value
    }
    const referrer = document.referrer ? cleanReferrer(document.referrer) : undefined
    if (referrer) live.initial_referrer = referrer
    const landing = cleanLandingPath(window.location.pathname)
    if (landing) live.initial_landing_path = landing
  } catch {
    // A blocked location must not stop signup.
  }
  return live
}

// Only capture for accounts that just signed up, so an old account logging in
// from a new device is never labelled with that device's campaign.
export function isFirstTouchEligible(
  userCreatedAt: string | undefined | null,
  now = Date.now()
): boolean {
  const created = userCreatedAt ? new Date(userCreatedAt).getTime() : NaN
  return Number.isFinite(created) && now - created < FIRST_TOUCH_MAX_ACCOUNT_AGE_MS
}

type AdminLike = {
  from: (table: "profiles") => {
    update: (values: Record<string, unknown>) => {
      eq: (col: string, val: string) => {
        is: (col: string, val: null) => PromiseLike<{ error: { message: string } | null }>
      }
    }
  }
}

// Guarded write: only touches a row whose first_touch_captured_at is still NULL.
export async function writeProfileFirstTouch(
  admin: AdminLike,
  userId: string,
  columns: ProfileFirstTouchColumns,
  nowIso = new Date().toISOString()
): Promise<boolean> {
  if (Object.keys(columns).length === 0) return false
  const { error } = await admin
    .from("profiles")
    .update({ ...columns, first_touch_captured_at: nowIso })
    .eq("id", userId)
    .is("first_touch_captured_at", null)
  if (error) throw new Error(`profile first-touch write failed: ${error.message}`)
  return true
}
